import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { BSPReport } from "@/models/api/report/BSPReport";
import { BSPTicket } from "@/models/api/report/BSPTicket";
import { BSPFinancialProperty } from "@/models/api/report/BSPFinancialProperty";
import { BSPBookingItemMatch } from "@/models/api/report/BSPBookingItemMatch";
import {
  getBSPReportList,
  getBSPTicketList,
  getBSPTicketInfo,
  getBSPBookingItemList,
  postManualBSPMatch,
  postRemoveBSPMatch,
  postMatchTickets,
  postDeleteBSPReport,
} from "@/requests/report/reportBSP";
import { FILTER_NAMES } from "@/static/filterNames";

export interface ReportsBSPStateInterface {
  //Report
  isReportTableLoading: boolean;
  reportTableData: Array<BSPReport>;
  reportTableCount: number;
  reportTableOptions: DataOptions;
  reportTableFilters: Array<CriterionItemRequest> | null;
  selectedReportId: number | null;
  //Ticket
  isTicketTableLoading: boolean;
  ticketTableData: Array<BSPTicket>;
  ticketTableCount: number;
  ticketTableOptions: DataOptions;
  ticketTableFilters: Array<CriterionItemRequest> | null;
  selectedTicketId: number | null;
  ticketInfo: BSPFinancialProperty | null;
  ticketNumber: string | null;
  //BookingItem
  isBookingItemTableLoading: boolean;
  bookingItemTableData: Array<BSPBookingItemMatch>;
  bookingItemTableCount: number;
  bookingItemTableOptions: DataOptions;
  bookingItemTableFilters: Array<CriterionItemRequest> | null;
}

export const ReportsBSPState: ReportsBSPStateInterface = {
  isReportTableLoading: false,
  reportTableData: [],
  reportTableCount: -1,
  reportTableOptions: new DefaultDataOptions(),
  reportTableFilters: null,
  selectedReportId: null,

  isTicketTableLoading: false,
  ticketTableData: [],
  ticketTableCount: -1,
  ticketTableOptions: new DefaultDataOptions(),
  ticketTableFilters: null,
  selectedTicketId: null,
  ticketInfo: null,
  ticketNumber: null,

  isBookingItemTableLoading: false,
  bookingItemTableData: [],
  bookingItemTableCount: -1,
  bookingItemTableOptions: new DefaultDataOptions(),
  bookingItemTableFilters: null,
};

const ReportsComponentModule = defineModule({
  state: ReportsBSPState as ReportsBSPStateInterface,
  namespaced: true as const,
  modules: {},
  getters: {},
  mutations: {
    //Report
    setIsReportTableLoading(state, param: boolean) {
      state.isReportTableLoading = param;
    },
    setReportTableData(state, param: Array<BSPReport>) {
      state.reportTableData = param;
    },
    setReportTableCount(state, param: number) {
      state.reportTableCount = param;
    },
    setReportTableOptions(state, param: DataOptions) {
      state.reportTableOptions = param;
    },
    setReportTableFilters(state, param: null | Array<CriterionItemRequest>) {
      state.reportTableFilters = param;
    },
    setSelectedReportId(state, param: number | null) {
      state.selectedReportId = param;
    },
    //Ticket
    setIsTicketTableLoading(state, param: boolean) {
      state.isTicketTableLoading = param;
    },
    setTicketTableData(state, param: Array<BSPTicket>) {
      state.ticketTableData = param;
    },
    setTicketTableCount(state, param: number) {
      state.ticketTableCount = param;
    },
    setTicketTableOptions(state, param: DataOptions) {
      state.ticketTableOptions = param;
    },
    setTicketTableFilters(state, param: null | Array<CriterionItemRequest>) {
      state.ticketTableFilters = param;
    },
    setSelectedTicketId(state, param: number | null) {
      state.selectedTicketId = param;
    },
    setTicketInfo(state, param: BSPFinancialProperty | null) {
      state.ticketInfo = param;
    },
    setTicketNumber(state, param: string | null) {
      state.ticketNumber = param;
    },
    //BookingItem
    setIsBookingItemTableLoading(state, param: boolean) {
      state.isBookingItemTableLoading = param;
    },
    setBookingItemTableData(state, param: Array<BSPBookingItemMatch>) {
      state.bookingItemTableData = param;
    },
    setBookingItemTableCount(state, param: number) {
      state.bookingItemTableCount = param;
    },
    setBookingItemTableOptions(state, param: DataOptions) {
      state.bookingItemTableOptions = param;
    },
    setBookingItemTableFilters(state, param: null | Array<CriterionItemRequest>) {
      state.bookingItemTableFilters = param;
    },
  },
  actions: {
    async getReportTableData(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsReportTableLoading(true);
      const response = await getBSPReportList({
        count: state.reportTableOptions.itemsPerPage,
        page: state.reportTableOptions.page - 1,
        criterions: state.reportTableFilters,
        filterName: FILTER_NAMES.BSPReportStandardFilter,
      });
      commit.setReportTableCount(response.totalCount);
      commit.setReportTableData(response.items);
      commit.setIsReportTableLoading(false);
      if (response.items.length > 0) {
        commit.setSelectedReportId(response.items[0].id);
      } else {
        commit.setSelectedReportId(null);
      }
    },
    async getTicketTableData(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsTicketTableLoading(true);
      const response = await getBSPTicketList(
        {
          count: state.ticketTableOptions.itemsPerPage,
          page: state.ticketTableOptions.page - 1,
          criterions: state.ticketTableFilters,
          filterName: FILTER_NAMES.BSPTicketsStandardFilter,
        },
        state.selectedReportId as number
      );
      commit.setTicketTableCount(response.totalCount);
      commit.setTicketTableData(response.items);
      commit.setIsTicketTableLoading(false);
      if (response.items.length > 0) {
        commit.setTicketNumber(response.items[0].ticketNumber);
        commit.setSelectedTicketId(response.items[0].id);
      } else {
        commit.setTicketNumber(null);
        commit.setSelectedTicketId(null);
      }
    },
    async getTicketInfo(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsTicketTableLoading(true);
      const response = await getBSPTicketInfo(state.selectedTicketId as number);
      commit.setTicketInfo(response);
      commit.setIsTicketTableLoading(false);
    },
    async getBookingItemTableData(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsBookingItemTableLoading(true);
      const response = await getBSPBookingItemList(
        {
          count: state.bookingItemTableOptions.itemsPerPage,
          page: state.bookingItemTableOptions.page - 1,
          criterions: state.bookingItemTableFilters,
          filterName: FILTER_NAMES.BSPBookingItemStandardFilter,
        },
        state.selectedTicketId as number
      );
      commit.setBookingItemTableCount(response.totalCount);
      commit.setBookingItemTableData(response.items);
      commit.setIsBookingItemTableLoading(false);
    },
    async deleteBSPReport(context, reportId: number) {
      const { rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await postDeleteBSPReport(reportId);
    },
    async manualBSPMatch(context, bookingItemId: number) {
      const { state, dispatch } = _moduleActionContext(context);
      await postManualBSPMatch(bookingItemId, state.selectedTicketId as number);
      const ticketId = state.selectedTicketId;
      await dispatch.getTicketTableData();
      if (ticketId == state.selectedTicketId) await dispatch.getBookingItemTableData();
    },
    async removeBSPMatch(context, bookingItemId: number) {
      const { state, dispatch } = _moduleActionContext(context);
      await postRemoveBSPMatch(bookingItemId);
      const ticketId = state.selectedTicketId;
      await dispatch.getTicketTableData();
      if (ticketId == state.selectedTicketId) await dispatch.getBookingItemTableData();
    },
    async matchTickets(context) {
      const { state, dispatch } = _moduleActionContext(context);
      await postMatchTickets(state.selectedReportId as number);
      const ticketId = state.selectedTicketId;
      await dispatch.getTicketTableData();
      if (ticketId == state.selectedTicketId) await dispatch.getBookingItemTableData();
    },
  },
});

export default ReportsComponentModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, ReportsComponentModule);
